/**
 * #2205 — build + paint the decode-region overlay for the scan diagnostics
 * page. Shows WHERE each decoder thought the barcode was, so RMR can see
 * partial / cut-off reads (the box covers only part of the tag, or runs to
 * the frame edge) instead of guessing.
 */

export interface OverlayBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  color: string;
  label: string;
  /** Dashed = a warning (cut-off / edge-touch). */
  dashed?: boolean;
}

export interface MsiBestBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  touchedLeftEdge: boolean;
  touchedRightEdge: boolean;
}

export interface ZbarBox {
  rawValue: string;
  format: string;
  boundingBox?: { x: number; y: number; width: number; height: number } | null;
  cornerPoints?: Array<{ x: number; y: number }> | null;
}

const GREEN = '#22c55e';
const AMBER = '#f59e0b';
const BLUE = '#3b82f6';

/**
 * Turn decoder outputs into drawable boxes. MSI box is green normally, amber +
 * dashed when it touched a frame edge (cut-off read). ZBar boxes are blue.
 * A minimum height keeps thin single-scanline boxes visible.
 */
export function buildOverlayBoxes(
  msi: MsiBestBox | null | undefined,
  zbar: ZbarBox[] = [],
  opts: { minHeight?: number } = {},
): OverlayBox[] {
  const minH = opts.minHeight ?? 24;
  const boxes: OverlayBox[] = [];

  if (msi) {
    const cutOff = msi.touchedLeftEdge || msi.touchedRightEdge;
    const yMid = (msi.y0 + msi.y1) / 2;
    const half = Math.max((msi.y1 - msi.y0) / 2, minH / 2);
    boxes.push({
      x0: msi.x0,
      x1: msi.x1,
      y0: yMid - half,
      y1: yMid + half,
      color: cutOff ? AMBER : GREEN,
      dashed: cutOff,
      label: cutOff ? `MSI · cut off${msi.touchedRightEdge ? ' (right)' : ''}${msi.touchedLeftEdge ? ' (left)' : ''}` : 'MSI',
    });
  }

  for (const z of zbar) {
    const bb = z.boundingBox;
    if (z.cornerPoints && z.cornerPoints.length >= 2) {
      const xs = z.cornerPoints.map((p) => p.x);
      const ys = z.cornerPoints.map((p) => p.y);
      boxes.push({
        x0: Math.min(...xs),
        x1: Math.max(...xs),
        y0: Math.min(...ys),
        y1: Math.max(...ys),
        color: BLUE,
        label: `ZBar ${z.format}`,
      });
    } else if (bb) {
      boxes.push({ x0: bb.x, y0: bb.y, x1: bb.x + bb.width, y1: bb.y + bb.height, color: BLUE, label: `ZBar ${z.format}` });
    }
  }
  return boxes;
}

/** Paint the boxes onto a 2D canvas context (presentation glue). */
export function paintOverlay(ctx: CanvasRenderingContext2D, boxes: OverlayBox[]): void {
  ctx.save();
  ctx.lineWidth = Math.max(2, Math.round(ctx.canvas.width / 320));
  ctx.font = `${Math.max(12, Math.round(ctx.canvas.width / 40))}px ui-monospace, monospace`;
  ctx.textBaseline = 'bottom';
  for (const b of boxes) {
    ctx.setLineDash(b.dashed ? [10, 6] : []);
    ctx.strokeStyle = b.color;
    ctx.strokeRect(b.x0, b.y0, b.x1 - b.x0, b.y1 - b.y0);
    const labelY = b.y0 > 18 ? b.y0 - 2 : b.y1 + 16;
    ctx.fillStyle = b.color;
    ctx.fillText(b.label, b.x0, labelY);
  }
  ctx.restore();
}

/**
 * Map a box from capture-canvas pixels to the displayed (object-fit: cover)
 * video box (#2210). `cover` scales by the LARGER ratio and center-crops the
 * overflow, so the mapping scales uniformly and offsets to match. Used by the
 * production BarcodeScanner to draw the decode region over the live preview.
 */
export function mapCoverBox(
  box: { x: number; y: number; w: number; h: number },
  srcW: number,
  srcH: number,
  dstW: number,
  dstH: number,
): { x: number; y: number; w: number; h: number } {
  if (srcW === 0 || srcH === 0) return { x: 0, y: 0, w: 0, h: 0 };
  const scale = Math.max(dstW / srcW, dstH / srcH);
  const offsetX = (dstW - srcW * scale) / 2;
  const offsetY = (dstH - srcH * scale) / 2;
  return { x: box.x * scale + offsetX, y: box.y * scale + offsetY, w: box.w * scale, h: box.h * scale };
}
